import { forwardRef, useEffect, useMemo } from "react";
import { DatePicker } from "antd";
import luxonGenerateConfig from "rc-picker/lib/generate/luxon";
import { DateTime } from "luxon";

import BaseError from "../BaseError";
import { BaseDateRangePickerProps } from "./types";
import { BaseFormError } from "../types";
import { RangeValueType } from "rc-picker/lib/PickerInput/RangePicker";

import "./styles.css";

const LuxonDatePicker =
  DatePicker.generatePicker<DateTime>(luxonGenerateConfig);

const { RangePicker } = LuxonDatePicker;

const BaseDateRangePicker = forwardRef<any, BaseDateRangePickerProps>(
  ({ value, onChange, error, ...props }: BaseDateRangePickerProps, ref) => {
    useEffect(() => {
      if (!value || !Array.isArray(value) || !onChange) return;

      const needsConversion = value.some(
        (val) => val && !DateTime.isDateTime(val),
      );
      if (needsConversion) {
        const convertedValues = value.map((val) => {
          if (!val) return null;
          if (DateTime.isDateTime(val)) return val;
          return DateTime.fromISO(val);
        }) as RangeValueType<DateTime<boolean>> | null | undefined;
        onChange(convertedValues);
      }
    }, [value]);

    const currentValue = useMemo(() => {
      if (!value || !Array.isArray(value)) return null;

      return value.map((val) => {
        if (!val) return null;
        if (DateTime.isDateTime(val)) return val;
        const converted = DateTime.fromISO(val);
        return converted.isValid ? converted : null;
      }) as RangeValueType<DateTime<boolean>> | null | undefined;
    }, [value]);

    const handleChange = (
      dates: RangeValueType<DateTime<boolean>> | null | undefined,
    ) => {
      if (!dates || !onChange) {
        onChange(dates);
        return;
      }

      const [startDate, endDate] = dates;
      const adjustedDates = [
        startDate,
        endDate ? endDate.endOf("day") : endDate,
      ] as RangeValueType<DateTime<boolean>> | null | undefined;

      onChange(adjustedDates);
    };

    return (
      <>
        <RangePicker
          className={`base-date-range-picker ${error && "base-date-range-picker-error"}`}
          onChange={handleChange}
          value={currentValue}
          {...props}
          ref={ref}
        />
        <BaseError error={error as BaseFormError} />
      </>
    );
  },
);

export default BaseDateRangePicker;
